import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ScanLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, statusOf } from "@/lib/api";
import { uploadFile } from "@/lib/upload13";
import { writesLocked } from "@/lib/splits";
import { useHub } from "@/components/AppShell";
import { Loader, NoAccess, PageHeader } from "@/components/common";
import { ReceiptReview } from "@/components/scan/Receipt";
import { Pantry } from "@/components/scan/Pantry";

const TABS = [["receipts", "Receipts"], ["pantry", "Pantry"]];

export default function Scanning() {
  const { hub } = useHub();
  const base = `/organizations/${hub.id}/scanning`;
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`${base}/receipts`).then(({ data }) => setD(data)).catch((e) => (statusOf(e) === 403 ? setDenied(true) : toast.error(errMsg(e)))), [base]);
  useEffect(() => { load(); }, [load]);
  if (denied) return <NoAccess />;
  if (!d) return <Loader />;
  const tab = sp.get("tab") || "receipts";
  const locked = writesLocked(hub) || !d.can_write;
  const scan = async (file) => {
    if (!file) return;
    setBusy(true);
    try { await api.post(`${base}/receipts`, { upload_id: await uploadFile(base, file) }); await load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="min-w-0" data-testid="scanning-page">
      <PageHeader eyebrow="Family" title="Scanning" rgb="var(--glow-money)" testid="scanning-title" />
      {locked && <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="scan-lapsed-banner"><AlertTriangle className="h-4 w-4" /> This hub is read-only, so scanning is paused.</div>}
      <div className="mb-5 flex max-w-full gap-1 overflow-x-auto rounded-full border border-q-line p-1" role="tablist">{TABS.map(([k, t]) => (
        <button key={k} role="tab" aria-selected={tab === k} className={`min-h-[44px] shrink-0 rounded-full px-4 text-sm font-semibold ${tab === k ? "bg-q-raised text-q-text" : "text-q-muted"}`} onClick={() => setSp({ tab: k })} data-testid={`scan-tab-${k}`}>{t}</button>))}</div>
      {tab === "receipts" && <div className="max-w-2xl space-y-4">
        <label className={`q-btn q-btn-primary min-h-[52px] w-full ${locked || busy ? "pointer-events-none opacity-50" : "cursor-pointer"}`} data-testid="scan-receipt-label">
          <ScanLine className="h-5 w-5" /> {busy ? "Reading your receipt…" : "Scan a receipt"}
          <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" disabled={locked || busy} onChange={(e) => { scan(e.target.files[0]); e.target.value = ""; }} data-testid="scan-receipt-input" /></label>
        {d.scans.map((s) => <ReceiptReview key={`${s.id}-${s.status}`} base={base} s={s} cats={d.categories} reload={load} />)}
      </div>}
      {tab === "pantry" && <Pantry base={base} canWrite={!locked} />}
    </div>
  );
}
